import { useCallback, useEffect, useRef, useState } from 'react';
import { getAiSession } from './ai.js';
import { clearPrivateMediaCache } from './private-media.jsx';
import { Store } from './mirror-store.js';
import {
  getMirrorSnapshot,
  hasSyncableData,
  preparePrivateSnapshot,
  putMirrorSnapshot,
  readSyncMeta,
  snapshotFingerprint,
  writeSyncMeta,
} from './sync.js';

const IDLE_SYNC = { state: 'local', label: 'Saved on this device', detail: '', progress: null };
const PAUSED_SYNC = { state: 'paused', label: 'Sync paused', detail: 'Your changes remain on this device.', progress: null };
const PAUSE_KEY_PREFIX = 'mirror_sync_paused:';

function pauseKey(userId) {
  return userId ? `${PAUSE_KEY_PREFIX}${String(userId)}` : '';
}

function readPaused(userId) {
  const key = pauseKey(userId);
  if (!key) return false;
  try { return localStorage.getItem(key) === '1'; } catch { return false; }
}

function writePaused(userId, paused) {
  const key = pauseKey(userId);
  if (!key) return;
  try {
    if (paused) localStorage.setItem(key, '1');
    else localStorage.removeItem(key);
  } catch { /* Sync can still pause for this session in hardened storage contexts. */ }
}

function isAbortError(error) {
  return error?.name === 'AbortError' || error?.code === 'ERR_CANCELED';
}

export function useMirrorSync({ ready, session, onState, onSession, showToast }) {
  const [sync, setSync] = useState(IDLE_SYNC);
  const [conflict, setConflict] = useState(null);
  const revisionRef = useRef(0);
  const syncReadyRef = useRef(false);
  const busyRef = useRef(false);
  const pendingRef = useRef(false);
  const suppressNextRef = useRef(false);
  const timerRef = useRef(null);
  const generationRef = useRef(0);
  const changeSeqRef = useRef(0);
  const cooldownUntilRef = useRef(0);
  const abortControllerRef = useRef(null);
  const pausedRef = useRef(readPaused(session.user?.id));
  const pausedUserRef = useRef(String(session.user?.id || ''));
  const [syncEpoch, setSyncEpoch] = useState(0);

  const currentUserId = String(session.user?.id || '');
  if (pausedUserRef.current !== currentUserId) {
    pausedUserRef.current = currentUserId;
    pausedRef.current = readPaused(currentUserId);
  }

  const activeController = () => {
    if (!abortControllerRef.current || abortControllerRef.current.signal.aborted) abortControllerRef.current = new AbortController();
    return abortControllerRef.current;
  };

  const applySnapshot = useCallback(async (snapshot, remote) => {
    suppressNextRef.current = true;
    Store.replaceState(snapshot);
    await Store.sealProfileState(snapshot, session.user.id);
    onState(Store.getState());
    const hash = await snapshotFingerprint(snapshot);
    revisionRef.current = remote.revision;
    writeSyncMeta(session.user.id, {
      revision: remote.revision,
      snapshotHash: hash,
      lastSyncedAt: remote.updatedAt || new Date().toISOString(),
    });
    setSync({ state: 'synced', label: 'All changes synced', detail: remote.updatedAt || '', progress: null });
  }, [onState, session.user?.id]);

  const raiseConflict = useCallback((remote, localSnapshot = Store.getState()) => {
    setConflict({ remote, localSnapshot });
    setSync({ state: 'attention', label: 'Sync needs attention', detail: 'Changes were found on another device.', progress: null });
  }, []);

  const pushCurrent = useCallback(async ({ baseRevision = revisionRef.current, stateOverride = null } = {}) => {
    if (session.user?.isDemo) return null;
    if (pausedRef.current) return null;
    if (!session.token || !session.user?.id || busyRef.current) {
      pendingRef.current = true;
      return null;
    }
    const cooldownWait = cooldownUntilRef.current - Date.now();
    if (cooldownWait > 0) {
      pendingRef.current = true;
      window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => void pushCurrent(), cooldownWait + 100);
      setSync({ state: 'cooldown', label: 'Private upload paused', detail: 'Automatic retry is scheduled.', progress: null });
      return null;
    }
    busyRef.current = true;
    pendingRef.current = false;
    const generation = generationRef.current;
    const controller = activeController();
    const startChangeSeq = changeSeqRef.current;
    let pushSucceeded = false;
    setSync({ state: 'syncing', label: 'Securing photos…', detail: '', progress: null });
    try {
      const { snapshot } = await preparePrivateSnapshot(stateOverride || Store.getState(), {
        signal: controller.signal,
        onProgress: progress => setSync({
          state: 'syncing',
          label: progress.total ? `Securing photos ${progress.completed}/${progress.total}` : 'Securing photos…',
          detail: 'Owner-only VPS media',
          progress,
        }),
      });
      setSync({ state: 'syncing', label: 'Syncing wardrobe…', detail: '', progress: null });
      const result = await putMirrorSnapshot(snapshot, baseRevision, { signal: controller.signal });
      if (generation !== generationRef.current || controller.signal.aborted) return null;
      pushSucceeded = true;
      const localUnchanged = changeSeqRef.current === startChangeSeq;
      if (localUnchanged) {
        suppressNextRef.current = true;
        Store.replaceState(snapshot);
        await Store.sealProfileState(snapshot, session.user.id);
        onState(Store.getState());
      } else {
        pendingRef.current = true;
      }
      const hash = await snapshotFingerprint(snapshot);
      revisionRef.current = result.revision;
      writeSyncMeta(session.user.id, {
        revision: result.revision,
        snapshotHash: hash,
        lastSyncedAt: result.updatedAt,
      });
      onSession(getAiSession());
      setConflict(null);
      setSync(localUnchanged
        ? { state: 'synced', label: 'All changes synced', detail: result.updatedAt || '', progress: null }
        : { state: 'syncing', label: 'Finishing newer changes…', detail: '', progress: null });
      return result;
    } catch (error) {
      if (isAbortError(error) || generation !== generationRef.current || controller.signal.aborted) {
        return null;
      } else if (error.status === 409) {
        pendingRef.current = false;
        try {
          const remote = await getMirrorSnapshot({ signal: controller.signal });
          if (generation !== generationRef.current || controller.signal.aborted) return null;
          raiseConflict(remote, stateOverride || Store.getState());
        } catch {
          if (generation !== generationRef.current || controller.signal.aborted) return null;
          setSync({ state: 'attention', label: 'Sync needs attention', detail: error.message, progress: null });
        }
      } else if (error.status === 429 && error.cooldown?.source === 'mirror') {
        const retryAt = Number(error.retryAt || (Date.now() + Number(error.retryAfterSeconds || 30) * 1000));
        cooldownUntilRef.current = retryAt;
        pendingRef.current = true;
        window.clearTimeout(timerRef.current);
        timerRef.current = window.setTimeout(() => void pushCurrent(), Math.max(100, retryAt - Date.now() + 100));
        setSync({ state: 'cooldown', label: 'Private upload paused', detail: 'Automatic retry is scheduled.', progress: null });
      } else if (!navigator.onLine || error instanceof TypeError) {
        pendingRef.current = true;
        setSync({ state: 'offline', label: 'Offline — changes are safe', detail: 'MIЯЯOЯ will sync when you reconnect.', progress: null });
      } else {
        pendingRef.current = true;
        setSync({ state: 'attention', label: 'Sync needs attention', detail: error.message || 'Could not sync yet.', progress: null });
      }
      return null;
    } finally {
      if (generation === generationRef.current) busyRef.current = false;
      if (generation === generationRef.current && pendingRef.current && pushSucceeded && syncReadyRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = window.setTimeout(() => void pushCurrent(), 250);
      }
    }
  }, [onSession, onState, raiseConflict, session.token, session.user?.id, session.user?.isDemo]);

  const schedulePush = useCallback(() => {
    if (session.user?.isDemo) return;
    if (pausedRef.current) return;
    if (!syncReadyRef.current || !session.token || conflict) return;
    pendingRef.current = true;
    window.clearTimeout(timerRef.current);
    const cooldownWait = cooldownUntilRef.current - Date.now();
    timerRef.current = window.setTimeout(() => void pushCurrent(), cooldownWait > 0 ? cooldownWait + 100 : 1400);
  }, [conflict, pushCurrent, session.token, session.user?.isDemo]);

  const abortSync = useCallback(() => {
    pausedRef.current = true;
    writePaused(session.user?.id, true);
    generationRef.current += 1;
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    window.clearTimeout(timerRef.current);
    busyRef.current = false;
    pendingRef.current = false;
    syncReadyRef.current = false;
    setConflict(null);
    setSync(PAUSED_SYNC);
  }, [session.user?.id]);

  const resumeSync = useCallback(() => {
    pausedRef.current = false;
    writePaused(session.user?.id, false);
    setSyncEpoch(current => current + 1);
  }, [session.user?.id]);

  useEffect(() => {
    const stateChange = event => {
      onState(event.detail?.state || Store.getState());
      if (suppressNextRef.current) {
        suppressNextRef.current = false;
        return;
      }
      changeSeqRef.current += 1;
      schedulePush();
    };
    window.addEventListener('mirror:state-change', stateChange);
    return () => window.removeEventListener('mirror:state-change', stateChange);
  }, [onState, schedulePush]);

  useEffect(() => {
    if (!ready || !session.token || !session.user?.id) {
      syncReadyRef.current = false;
      setConflict(null);
      setSync(session.token ? IDLE_SYNC : { state: 'locked', label: 'Sign in to sync', detail: 'Local changes remain on this device.', progress: null });
      return undefined;
    }
    if (pausedRef.current) {
      syncReadyRef.current = false;
      setConflict(null);
      setSync(PAUSED_SYNC);
      return undefined;
    }
    const generation = ++generationRef.current;
    let cancelled = false;
    syncReadyRef.current = false;
    const controller = activeController();

    const begin = async () => {
      const initialChangeSeq = changeSeqRef.current;
      setSync({ state: 'syncing', label: 'Checking for changes…', detail: '', progress: null });
      try {
        const remote = await getMirrorSnapshot({ signal: controller.signal });
        if (cancelled || generation !== generationRef.current || controller.signal.aborted) return;
        if (remote.snapshot && changeSeqRef.current !== initialChangeSeq) {
          raiseConflict(remote, Store.getState());
          return;
        }
        const local = Store.getState();
        const meta = readSyncMeta(session.user.id);
        const localHash = await snapshotFingerprint(local);
        revisionRef.current = remote.revision || 0;

        if (session.user?.isDemo) {
          if (remote.snapshot) await applySnapshot(remote.snapshot, remote);
          else setSync({ state: 'attention', label: 'Demo unavailable', detail: 'The seeded demonstration could not be loaded.', progress: null });
          return;
        }

        if (!remote.snapshot) {
          if (hasSyncableData(local)) await pushCurrent({ baseRevision: remote.revision || 0, stateOverride: local });
          else {
            writeSyncMeta(session.user.id, { revision: remote.revision || 0, snapshotHash: localHash, lastSyncedAt: remote.updatedAt });
            setSync({ state: 'synced', label: 'All changes synced', detail: '', progress: null });
          }
        } else if (!hasSyncableData(local)) {
          await applySnapshot(remote.snapshot, remote);
        } else if (meta?.revision === remote.revision) {
          if (meta.snapshotHash === localHash) {
            setSync({ state: 'synced', label: 'All changes synced', detail: remote.updatedAt || '', progress: null });
          } else {
            await pushCurrent({ baseRevision: remote.revision, stateOverride: local });
          }
        } else if (meta?.snapshotHash === localHash) {
          await applySnapshot(remote.snapshot, remote);
        } else {
          raiseConflict(remote, local);
        }
      } catch (error) {
        if (cancelled || generation !== generationRef.current || isAbortError(error) || controller.signal.aborted) return;
        if (!navigator.onLine || error instanceof TypeError) {
          pendingRef.current = true;
          setSync({ state: 'offline', label: 'Offline — changes are safe', detail: 'MIЯЯOЯ will sync when you reconnect.', progress: null });
        } else {
          setSync({ state: 'attention', label: 'Sync needs attention', detail: error.message || 'Could not check the VPS.', progress: null });
        }
      } finally {
        if (!cancelled && generation === generationRef.current && !controller.signal.aborted) syncReadyRef.current = true;
      }
    };
    void begin();
    return () => {
      cancelled = true;
      syncReadyRef.current = false;
      window.clearTimeout(timerRef.current);
      if (abortControllerRef.current === controller) {
        controller.abort();
        abortControllerRef.current = null;
      }
    };
  }, [applySnapshot, pushCurrent, raiseConflict, ready, session.token, session.user?.id, session.user?.isDemo, syncEpoch]);

  useEffect(() => {
    const reconnect = () => {
      if (pendingRef.current || sync.state === 'offline') void pushCurrent();
    };
    const visible = () => {
      if (document.visibilityState === 'visible' && pendingRef.current) void pushCurrent();
    };
    window.addEventListener('online', reconnect);
    document.addEventListener('visibilitychange', visible);
    return () => {
      window.removeEventListener('online', reconnect);
      document.removeEventListener('visibilitychange', visible);
    };
  }, [pushCurrent, sync.state]);

  const resolveConflict = useCallback(async choice => {
    if (!conflict) return;
    if (choice === 'cloud') {
      await applySnapshot(conflict.remote.snapshot, conflict.remote);
      setConflict(null);
      showToast('This device now matches the synced wardrobe.');
      return;
    }
    const result = await pushCurrent({ baseRevision: conflict.remote.revision, stateOverride: conflict.localSnapshot });
    if (result) showToast('This device is now the synced wardrobe.');
  }, [applySnapshot, conflict, pushCurrent, showToast]);

  const clearForProfileChange = useCallback(() => {
    pausedRef.current = false;
    generationRef.current += 1;
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    window.clearTimeout(timerRef.current);
    busyRef.current = false;
    pendingRef.current = false;
    syncReadyRef.current = false;
    clearPrivateMediaCache();
  }, []);

  return { sync, conflict, resolveConflict, pushNow: pushCurrent, abortSync, resumeSync, clearForProfileChange };
}
